import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { API_BASE_URL, fetchJson } from './api';

function getAuthHeader() {
    try {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function formatDuration(avgSeconds) {
    const s = Number(avgSeconds);
    if (!Number.isFinite(s) || s < 0) return '—';

    const totalMinutes = Math.round(s / 60);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes - days * 60 * 24) / 60);
    const minutes = Math.max(0, totalMinutes - days * 60 * 24 - hours * 60);

    const parts = [];
    if (days) parts.push(`${days}d`);
    if (hours || days) parts.push(`${hours}h`);
    if (!days) parts.push(`${minutes}m`);

    return parts.join(' ');
}

function formatResetAt(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
}

function MetricRow({ label, value, sampleCount }) {
    return (
        <tr>
            <td style={{ padding: '13px 16px', borderBottom: '1px solid rgba(15, 23, 42, 0.05)', color: 'var(--calendar-text)' }}>
                {label}
            </td>
            <td style={{ padding: '13px 16px', borderBottom: '1px solid rgba(15, 23, 42, 0.05)', width: '30%', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap', color: 'var(--calendar-text)' }}>
                {value}
            </td>
        </tr>
    );
}

export default function KpiMetricsCard({ style, authUser }) {
    const [loading, setLoading] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [kpis, setKpis] = useState(null);
    const [resetAt, setResetAt] = useState('');

    const authRole = String(authUser?.role || '').trim().toLowerCase();
    const isAdmin = authRole === 'admin';

    async function load() {
        setLoading(true);
        setError('');
        try {
            const json = await fetchJson(`${API_BASE_URL}/api/kpi/overview`, {
                method: 'GET',
                headers: { ...getAuthHeader() }
            });
            setKpis(json?.kpis || null);
            setResetAt(String(json?.reset_at || '').trim());
        } catch (e) {
            setKpis(null);
            setResetAt('');
            setError(e?.message || 'Failed to load KPI metrics');
        } finally {
            setLoading(false);
        }
    }

    async function handleReset() {
        const confirmed = window.confirm('Reset KPI metrics now? Existing KPI averages will be recalculated using only data created after this reset.');
        if (!confirmed) return;

        setResetting(true);
        setError('');
        setMessage('');
        try {
            const json = await fetchJson(`${API_BASE_URL}/api/kpi/overview/reset`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeader()
                },
                body: JSON.stringify({})
            });
            setKpis(json?.kpis || null);
            setResetAt(String(json?.reset_at || '').trim());
            setMessage('KPI metrics have been reset. New averages now use data captured after this reset.');
        } catch (e) {
            setError(e?.message || 'Failed to reset KPI metrics');
        } finally {
            setResetting(false);
        }
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const q = kpis?.questionnaire_response_time || {};
    const ini = kpis?.initial_report_turnaround_time || {};
    const intm = kpis?.interim_report_turnaround_time || {};

    const table = (
        <div style={{ marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                    <tr>
                        <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: 11, textAlign: 'left', width: '70%' }}>Metric</th>
                        <th style={{ padding: '10px 14px', borderBottom: '1px solid rgba(15, 23, 42, 0.06)', color: 'var(--muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: 11, textAlign: 'right', width: '30%' }}>Average</th>
                    </tr>
                </thead>
                <tbody>
                    <MetricRow
                        label="Average Questionnaire Response Time"
                        value={formatDuration(q.avg_seconds)}
                        sampleCount={q.sample_count}
                    />
                    <MetricRow
                        label="Average Initial Report Turnaround Time"
                        value={formatDuration(ini.avg_seconds)}
                        sampleCount={ini.sample_count}
                    />
                    <MetricRow
                        label="Average Interim Report Turnaround Time"
                        value={formatDuration(intm.avg_seconds)}
                        sampleCount={intm.sample_count}
                    />
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="card" style={{ marginBottom: 16, width: '100%', overflow: 'hidden', ...(style || {}) }}>
            <Box sx={{ px: { xs: 0.25, sm: 0.25 }, pt: 0.25, pb: 1, mb: 1, borderBottom: '1px solid rgba(15, 23, 42, 0.05)' }}>
                <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <h3 style={{ margin: 0, fontSize: '1.04rem', fontWeight: 800, letterSpacing: '-0.01em', textAlign: 'center' }}>KPI Metrics</h3>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, gap: 1, flexWrap: 'wrap', width: '100%' }}>
                    {isAdmin ? (
                        <Button
                            type="button"
                            onClick={handleReset}
                            disabled={loading || resetting}
                            size="small"
                            color="error"
                            variant="outlined"
                            aria-label="Reset KPI metrics"
                            sx={{
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: 'center',
                                minWidth: { sm: 120 },
                                px: { xs: 1.25, sm: 1.5 },
                                py: { xs: 0.5, sm: 0.5 }
                            }}
                        >
                            {resetting ? <CircularProgress size={16} /> : 'Reset'}
                        </Button>
                    ) : null}

                    <Button
                        type="button"
                        onClick={load}
                        disabled={loading || resetting}
                        size="small"
                        variant="outlined"
                        aria-label="Refresh KPI metrics"
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: 'center',
                            minWidth: { sm: 120 },
                            px: { xs: 1.25, sm: 1.5 },
                            py: { xs: 0.5, sm: 0.5 }
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={16} />
                        ) : (
                            <RefreshIcon sx={{ fontSize: 18 }} />
                        )}
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 1 }}>
                            {loading ? 'Loading…' : 'Refresh'}
                        </Box>
                    </Button>
                </Box>
            </Box>

            {resetAt ? (
                <div style={{ marginTop: 2, marginBottom: 10, color: 'var(--muted)', fontSize: 12 }}>
                    KPI baseline reset: {formatResetAt(resetAt)}
                </div>
            ) : null}

            {message ? (
                <div style={{ marginTop: 10, padding: 10, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', borderRadius: 8 }}>
                    {message}
                </div>
            ) : null}

            {error ? (
                <div style={{ marginTop: 10, padding: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#991b1b', borderRadius: 8 }}>
                    {error}
                </div>
            ) : null}

            {loading && !kpis ? (
                <div style={{ marginTop: 10, color: 'var(--muted)' }}>Loading KPI metrics…</div>
            ) : (
                <div style={{ borderRadius: 18, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.56)', background: 'linear-gradient(180deg, rgba(255,255,255,0.56), rgba(244,248,252,0.72))' }}>
                    {table}
                </div>
            )}
        </div>
    );
}
